import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/layout/PageHeader';
import { ContentWrapper } from '@/components/layout/ContentWrapper';
import {
  TrendingUp, Upload, BarChart2, CreditCard, Activity, CheckCircle,
  AlertCircle, ArrowRight, FileText, DollarSign,
} from 'lucide-react';

// Mock data
const portfolioMetrics = [
  {
    label: 'Total Leads This Month',
    value: '4,800',
    change: '+12.5%',
    changeType: 'positive' as const,
    icon: BarChart2,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
  },
  {
    label: 'Qualified Claims Discovered',
    value: '748',
    change: '+23.1%',
    changeType: 'positive' as const,
    icon: CheckCircle,
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
  },
  {
    label: 'Qualification Success Rate',
    value: '15.6%',
    change: '+2.3%',
    changeType: 'positive' as const,
    icon: TrendingUp,
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
  },
  {
    label: 'Available Credits',
    value: '2,847',
    change: '~19 weeks remaining',
    changeType: 'neutral' as const,
    icon: CreditCard,
    iconBg: 'bg-cyan-100',
    iconColor: 'text-cyan-600',
  },
];

const recentBatches = [
  { id: 'BATCH-001', name: 'RealPage Claims January', type: 'RealPage', status: 'completed', processed: 2347, qualified: 312, rate: 13.3, date: '2 hours ago' },
  { id: 'BATCH-002', name: 'Q4 Client Database', type: 'RealPage', status: 'completed', processed: 1850, qualified: 247, rate: 13.4, date: '6 hours ago' },
  { id: 'BATCH-003', name: 'PFAS Exposure Claims', type: 'PFAS', status: 'processing', processed: 456, qualified: 0, rate: 0, date: '1 day ago' },
];

const notifications = [
  { type: 'success' as const, title: 'New qualified claims discovered', message: '47 additional RealPage claims identified in recent processing', time: '2 hours ago', icon: CheckCircle },
  { type: 'info' as const, title: 'Processing optimization deployed', message: 'Batch processing speed improved by 15%', time: '1 day ago', icon: TrendingUp },
  { type: 'warning' as const, title: 'Credit balance notification', message: 'Consider purchasing additional credits (2,847 remaining)', time: '2 days ago', icon: AlertCircle },
];

const quickActions = [
  { label: 'Upload New Database', icon: Upload, href: '/claims/upload', color: 'bg-blue-600 hover:bg-blue-700' },
  { label: 'View Results', icon: FileText, href: '/claims/results', color: 'bg-green-600 hover:bg-green-700' },
  { label: 'Purchase Credits', icon: DollarSign, href: '/account/purchase', color: 'bg-purple-600 hover:bg-purple-700' },
];

const trendData = [
  { week: 'Week 1', qualified: 142, processed: 1200 },
  { week: 'Week 2', qualified: 189, processed: 1350 },
  { week: 'Week 3', qualified: 167, processed: 1100 },
  { week: 'Week 4', qualified: 250, processed: 1150 },
];

const DashboardOverview: React.FC = () => {
  return (
    <ContentWrapper>
      <PageHeader
        title="Dashboard Overview"
        subtitle="Monitor your claims discovery performance and system activity"
        breadcrumbs={[{ label: 'Dashboard', isActive: true }]}
      />

      {/* Portfolio Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {portfolioMetrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <Card key={index} className="bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-lg ${metric.iconBg}`}>
                    <Icon className={`h-6 w-6 ${metric.iconColor}`} />
                  </div>
                  {metric.changeType === 'positive' && (
                    <Badge variant="success" className="text-xs">
                      {metric.change}
                    </Badge>
                  )}
                  {metric.changeType === 'neutral' && (
                    <span className="text-xs text-gray-500">{metric.change}</span>
                  )}
                </div>
                <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                <p className="text-sm text-gray-500 mt-1">{metric.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {quickActions.map((action, index) => {
          const Icon = action.icon;
          return (
            <a key={index} href={action.href}>
              <Button
                className={`w-full h-14 ${action.color} text-white justify-between px-6`}
              >
                <span className="flex items-center gap-3">
                  <Icon className="h-5 w-5" />
                  {action.label}
                </span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </a>
          );
        })}
      </div>

      {/* Activity & Analytics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Recent Batches */}
        <Card className="bg-white border border-gray-200 shadow-sm">
          <CardHeader className="border-b border-gray-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-gray-900">Recent Batches</CardTitle>
              <a href="/claims/processing" className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
                View All <ArrowRight className="h-3 w-3" />
              </a>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-100">
              {recentBatches.map((batch) => (
                <div key={batch.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="font-medium text-gray-900 text-sm">{batch.name}</span>
                      <span className="ml-2 text-xs text-gray-500">{batch.id}</span>
                    </div>
                    <Badge variant={batch.status === 'completed' ? 'success' : 'warning'}>
                      {batch.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>{batch.processed.toLocaleString()} processed</span>
                    {batch.status === 'completed' && (
                      <>
                        <span>{batch.qualified} qualified</span>
                        <span className="text-green-600 font-medium">{batch.rate}% rate</span>
                      </>
                    )}
                    <span className="ml-auto">{batch.date}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Trend Chart (simple bar visualization) */}
        <Card className="bg-white border border-gray-200 shadow-sm">
          <CardHeader className="border-b border-gray-100">
            <CardTitle className="text-lg text-gray-900">Monthly Trends</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {trendData.map((week) => (
                <div key={week.week}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-600">{week.week}</span>
                    <span className="font-medium text-gray-900">{week.qualified} qualified</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-cyan-400 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${(week.qualified / 300) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{week.processed.toLocaleString()} total processed</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Notifications */}
      <Card className="bg-white border border-gray-200 shadow-sm">
        <CardHeader className="border-b border-gray-100">
          <CardTitle className="text-lg text-gray-900">System Notifications</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-gray-100">
            {notifications.map((notif, index) => {
              const Icon = notif.icon;
              const colorMap = {
                success: 'bg-green-50 text-green-600',
                info: 'bg-blue-50 text-blue-600',
                warning: 'bg-yellow-50 text-yellow-600',
              };
              return (
                <div key={index} className="p-4 flex items-start gap-4 hover:bg-gray-50 transition-colors">
                  <div className={`p-2 rounded-lg flex-shrink-0 ${colorMap[notif.type]}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 text-sm">{notif.title}</p>
                    <p className="text-sm text-gray-500 mt-0.5">{notif.message}</p>
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">{notif.time}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </ContentWrapper>
  );
};

export default DashboardOverview;